import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createVuetify } from 'vuetify';
// Vuetify's class-font icon component (VClassIcon) is not a public export;
// the `fa` iconset is exactly `{ component: VClassIcon }`, so reuse it for
// Boxicons. It declares the icon/tag props and forwards Vuetify-provided
// attrs (class/style/aria) to the rendered <i> element.
import { fa as classFontIconSet } from 'vuetify/iconsets/fa';

import App from './App.vue';
import router from './router/main.js';
import { initializeLanguageModule, vuePluginI18n } from './languages/main.js';

import 'boxicons/css/boxicons.min.css';
import '@fontsource/public-sans/400.css';
import '@fontsource/public-sans/500.css';
import 'vuetify/styles';

initializeLanguageModule();

// Boxicons implementations of Vuetify's internal icon aliases ($error,
// $close, …). This app loads Boxicons (the Sneat icon set, matching Portal),
// not the MDI font the stock aliases resolve to — without this mapping,
// internal icons such as the error-alert icon render as an empty box.
// Alias map mirrors portal's src/plugins/vuetify/vuetify.ts.
const iconAliases = {
    collapse: 'bx bx-chevron-up',
    complete: 'bx bx-check',
    cancel: 'bx bx-x-circle',
    close: 'bx bx-x',
    delete: 'bx bx-x-circle',
    clear: 'bx bx-x-circle',
    success: 'bx bx-check-circle',
    info: 'bx bx-info-circle',
    warning: 'bx bx-error',
    error: 'bx bx-x-circle',
    prev: 'bx bx-chevron-left',
    next: 'bx bx-chevron-right',
    checkboxOn: 'bx bxs-checkbox-checked',
    checkboxOff: 'bx bx-checkbox',
    checkboxIndeterminate: 'bx bxs-checkbox-minus',
    delimiter: 'bx bxs-circle',
    sortAsc: 'bx bx-up-arrow-alt',
    sortDesc: 'bx bx-down-arrow-alt',
    expand: 'bx bx-chevron-down',
    menu: 'bx bx-menu',
    subgroup: 'bx bx-caret-down',
    dropdown: 'bx bx-chevron-down',
    radioOn: 'bx bxs-radio-circle-marked',
    radioOff: 'bx bx-radio-circle',
    edit: 'bx bx-pencil',
    ratingEmpty: 'bx bx-star',
    ratingFull: 'bx bxs-star',
    ratingHalf: 'bx bxs-star-half',
    loading: 'bx bx-loader-alt',
    first: 'bx bx-first-page',
    last: 'bx bx-last-page',
    unfold: 'bx bx-move-vertical',
    file: 'bx bx-paperclip',
    plus: 'bx bx-plus',
    minus: 'bx bx-minus',
    calendar: 'bx bx-calendar',
    treeviewCollapse: 'bx bx-chevron-down',
    treeviewExpand: 'bx bx-chevron-right',
};

const pinia = createPinia();
const vuetify = createVuetify({
    icons: {
        defaultSet: 'boxicons',
        aliases: iconAliases,
        sets: {
            boxicons: classFontIconSet,
        },
    },
    theme: {
        defaultTheme: 'light',
        themes: {
            light: {
                dark: false,
                colors: {
                    primary: '#007FFF',
                    error: '#FF1D1D',
                },
            },
        },
    },
});

const app = createApp(App);
app.use(vuetify);
app.use(router);
app.use(vuePluginI18n());
app.use(pinia);
app.mount('#app');
